import {
  getLandingSiteAgentProjectId,
  SITEGLUE_LANDING_SITE_AGENT_PROJECT_ID,
} from "~/lib/marketplace/constants";

/** Routes where no concierge sidebar should render. */
const EXCLUDED_PREFIXES = [
  "/w/",
  "/watch/",
  "/sign-in",
  "/sign-up",
  "/accept-invitation",
  "/chat/",
  "/widget-preview",
  "/widget-test",
  "/widget-dev-test",
  "/onboarding/",
  "/team/onboarding",
  "/employers",
  "/purposeai",
  "/projects",
  "/settings",
  "/billing",
  "/admin",
] as const;

const BUILDER_ROUTE = /\/projects\/[^/]+\/builder\/?$/;
const AGENT_PROFILE_ROUTE = /^\/agent\/([^/]+)\/Agent_Profile\/?$/;
/** Public legacy CV-style profile — uses floating w.js, not the site sidebar. */
const LEGACY_AGENT_PROFILE_ROUTE = AGENT_PROFILE_ROUTE;
const MARKETPLACE_AGENT_PROFILE_ROUTE =
  /^\/agents\/([^/]+)(?:\/hire)?\/?$/;
const MARKETPLACE_AGENT_RESERVED_IDS = new Set(["hire"]);
const HIRE_CUSTOM_ROUTE = /^\/agents\/hire\/custom\/?$/;
/** Clone / custom hire builder — must not load template concierge widget before preview generate. */
const MARKETPLACE_HIRE_BUILDER_ROUTE = /^\/agents\/([^/]+)\/hire\/?$/;

export function isMarketplaceHireBuilderRoute(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  if (HIRE_CUSTOM_ROUTE.test(pathname)) return true;
  if (pathname === "/onboard" || pathname.startsWith("/onboard/")) return true;
  return MARKETPLACE_HIRE_BUILDER_ROUTE.test(pathname);
}

/** Legacy agent CV / profile demos — page embeds its own widget; no site concierge. */
const AGENT_DEMO_ROUTE = /^\/agent\//;

export const AGENT_SIDEBAR_OPEN_EVENT = "easygpt-agent-sidebar-open";

export type AgentSidebarOpenDetail = {
  projectId?: string;
  /**
   * When true on marketing (non-profile) pages, the site sidebar may switch
   * from the default concierge project to this projectId for Live Demo.
   */
  allowProjectSwitch?: boolean;
  tab?:
    | "aiChat"
    | "talk"
    | "videoCall"
    | "welcomeVideo"
    | "contact"
    | "call"
    | "sms"
    | "email"
    | "whatsapp"
    | "messenger";
  message?: string;
};

export function getAgentProfileProjectId(pathname: string | null | undefined): string | null {
  if (!pathname) return null;
  if (isMarketplaceHireBuilderRoute(pathname)) return null;
  const legacyMatch = pathname.match(AGENT_PROFILE_ROUTE);
  if (legacyMatch?.[1]) return legacyMatch[1];
  const marketplaceMatch = pathname.match(MARKETPLACE_AGENT_PROFILE_ROUTE);
  if (marketplaceMatch?.[1]) {
    const id = marketplaceMatch[1];
    if (!MARKETPLACE_AGENT_RESERVED_IDS.has(id)) return id;
  }
  return null;
}

export function isMarketplaceAgentProfileRoute(pathname: string | null | undefined): boolean {
  return getAgentProfileProjectId(pathname) != null;
}

export function isAgentDemoRoute(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return AGENT_DEMO_ROUTE.test(pathname);
}

export function isAgentProfileRoute(pathname: string | null | undefined): boolean {
  return getAgentProfileProjectId(pathname) !== null;
}

export function isLegacyAgentProfileRoute(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return LEGACY_AGENT_PROFILE_ROUTE.test(pathname);
}

export function isSidebarExcludedRoute(pathname: string | null | undefined): boolean {
  if (!pathname) return true;
  if (EXCLUDED_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return true;
  if (BUILDER_ROUTE.test(pathname)) return true;
  return false;
}

/** SiteGlue `/onboard` — show Ask Victoria FAB (same concierge as marketing home). */
export function isSiteGlueOnboardRoute(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return pathname === "/onboard" || pathname.startsWith("/onboard/");
}

/** Marketplace agent pages use LandingAgentSidebar; legacy /agent/.../Agent_Profile uses w.js. */
export function shouldShowSiteAgentSidebar(pathname: string | null | undefined): boolean {
  if (isSidebarExcludedRoute(pathname)) return false;
  // /onboard: no Ask Victoria FAB on load — open via left-rail "Get Help" push sidebar instead.
  if (isSiteGlueOnboardRoute(pathname)) return false;
  if (isMarketplaceHireBuilderRoute(pathname)) return false;
  if (isLegacyAgentProfileRoute(pathname)) return false;
  if (isAgentProfileRoute(pathname)) return true;
  if (isAgentDemoRoute(pathname)) return false;
  return true;
}

export function resolveConciergeProjectId(pathname: string | null | undefined): string | null {
  if (!shouldShowSiteAgentSidebar(pathname)) return null;

  const profileProjectId = getAgentProfileProjectId(pathname);
  if (profileProjectId) return profileProjectId;

  // /onboard runs on app.easygpt.builders — always use Victoria (SiteGlue AI), not Denise.
  if (isSiteGlueOnboardRoute(pathname)) {
    return SITEGLUE_LANDING_SITE_AGENT_PROJECT_ID;
  }

  return getLandingSiteAgentProjectId();
}

export function isBuilderEmbedContext(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("embed") === "builder";
}

export function sidebarStorageKey(projectId: string): string {
  return `easygpt-sidebar-mode-${projectId}`;
}

export const AGENT_PROFILE_SIDEBAR_MIN_WIDTH_PX = 300;
export const AGENT_PROFILE_SIDEBAR_MAX_WIDTH_PX = 770;
export const AGENT_PROFILE_SIDEBAR_DEFAULT_WIDTH_PX = 470;

export function sidebarWidthStorageKey(projectId: string): string {
  return `easygpt-agent-profile-sidebar-width-${projectId}`;
}

export function readStoredSidebarWidthPx(projectId: string): number | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(sidebarWidthStorageKey(projectId));
  if (!raw) return null;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed)) return null;
  return Math.min(
    AGENT_PROFILE_SIDEBAR_MAX_WIDTH_PX,
    Math.max(AGENT_PROFILE_SIDEBAR_MIN_WIDTH_PX, parsed),
  );
}

export function storeSidebarWidthPx(projectId: string, widthPx: number): void {
  if (typeof window === "undefined") return;
  const clamped = Math.min(
    AGENT_PROFILE_SIDEBAR_MAX_WIDTH_PX,
    Math.max(AGENT_PROFILE_SIDEBAR_MIN_WIDTH_PX, widthPx),
  );
  window.localStorage.setItem(sidebarWidthStorageKey(projectId), String(clamped));
}
